import React, { useEffect, useState } from 'react';
import { Layout, Menu, Dropdown, Modal } from 'antd';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import cookie from 'js-cookie';
import {
  FolderKanban,
  Database,
  Tags,
  User,
  ChevronLeft,
  ChevronRight,
  Activity,
  Globe,
  BarChart2,
  Users,
} from 'lucide-react';
import { routes, ROLES, TOKEN, REFRESH_TOKEN } from '../../../utils/constants/config';
import { checkRole, actionLogout, requestLogin } from '../../../view/system/systemAction';
import afyaLogo from '../../../assets/icons/afya_tech_logo.svg';
import './LeftMenu.scss';

const LeftMenu = (props) => {
  const { location, userInfo, profile } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState();

  const getUserRoles = () => {
    const roles = profile?.realm_roles || userInfo?.realm_roles || [];
    return Array.isArray(roles) ? roles : [];
  };

  const isRadiologistUser = () => {
    const roles = getUserRoles();
    const radiologistRoles = ['ANNOTATOR', 'REVIEWER', 'LABELER', 'Labeler'];
    return roles.some((role) => radiologistRoles.includes(role));
  };

  const isAdminUser = () => {
    const roles = getUserRoles();
    const adminRoles = ['PROJECT_OWNER', 'MANAGER', 'ADMIN', 'Administrator'];
    return roles.some((role) => adminRoles.includes(role));
  };

  // Referring physicians (read-only): clean sidebar — only the studies list,
  // no annotation/admin tooling. Gated so all other roles are unaffected.
  const isPhysicianUser = () => getUserRoles().includes('REFERRING_PHYSICIAN');

  const isTeleradiologyUser = () =>
    (profile?.hospital_id || userInfo?.hospital_id) === 'teleradiology';

  useEffect(() => {
    const { pathname } = location;
    if (pathname?.indexOf(routes.STUDY_LIST) > -1) {
      setSelectedKeys(routes.PROJECTS);
    } else {
      setSelectedKeys(pathname);
    }
  }, [location]);

  const handleNavClick = (route) => {
    setSelectedKeys(route);
    props.history.push(route);
  };

  /* Sidebar nav order — controls the visual order ONLY.
   * Routes, permissions (`isShow`), and click handlers are unchanged.
   * Order: Admin Dashboard → All Studies / My Worklist (mutually
   * exclusive by role) → My Projects → Label management. */
  const navItems = [
    {
      icon: Activity,
      label: 'Admin Dashboard',
      description: 'Overview',
      route: routes.ADMIN_DASHBOARD,
      isShow:
        !isTeleradiologyUser() &&
        (checkRole(userInfo, ROLES.PO) || checkRole(userInfo, ROLES.PO_PARTNER)),
    },
    {
      icon: Database,
      label: 'All Studies',
      description: 'Clinical study list',
      route: routes.STUDIES,
      isShow: (!isRadiologistUser() || isAdminUser()) && !isTeleradiologyUser(),
    },
    {
      icon: Database,
      label: 'My Worklist',
      description: 'Assigned studies',
      route: routes.WORKLIST,
      isShow: isRadiologistUser() && !isAdminUser() && !isTeleradiologyUser(),
    },
    {
      icon: FolderKanban,
      label: <FormattedMessage id="IDS_MY_PROJECTS" />,
      description: 'Annotation tasks',
      route: routes.PROJECTS,
      isShow: !isTeleradiologyUser() && !isPhysicianUser(),
    },
    {
      icon: Tags,
      label: <FormattedMessage id="IDS_LABEL_MANAGEMENT" />,
      description: 'Organize studies',
      route: routes.LABEL_MANAGEMENT,
      isShow:
        !isTeleradiologyUser() &&
        (checkRole(userInfo, ROLES.PO) || checkRole(userInfo, ROLES.PO_PARTNER)),
    },
    {
      icon: Globe,
      label: 'Teleradiology',
      description: 'Remote reading queue',
      route: routes.TELERADIOLOGY,
      isShow: isTeleradiologyUser(),
    },
    {
      icon: BarChart2,
      label: 'Analytics',
      description: 'Performance & billing',
      route: routes.TELERADIOLOGY_ANALYTICS,
      isShow: isTeleradiologyUser() && (checkRole(userInfo, ROLES.PO) || checkRole(userInfo, ROLES.PO_PARTNER)),
    },
    {
      icon: Users,
      label: 'Team',
      description: 'Manage radiologists',
      route: routes.TELERADIOLOGY_TEAM,
      isShow: isTeleradiologyUser() && (checkRole(userInfo, ROLES.PO) || checkRole(userInfo, ROLES.PO_PARTNER)),
    },
  ];

  const userName = profile?.name || profile?.preferred_username || 'User';
  const userEmail = profile?.email || '';

  const handleLogoutClick = ({ key }) => {
    if (key === routes.LOGOUT) {
      Modal.confirm({
        title: 'Are you sure?',
        content: null,
        onOk: () => {
          if (cookie.get(REFRESH_TOKEN)) {
            actionLogout();
          } else {
            cookie.remove(TOKEN);
            cookie.remove(REFRESH_TOKEN);
            requestLogin();
          }
        },
        onCancel: () => {},
      });
    }
  };

  const userMenu = (
    <Menu onClick={handleLogoutClick}>
      <Menu.Item key={routes.LOGOUT}>
        <FormattedMessage id="IDS_COMMON_LOGOUT" />
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout.Sider
      width={220}
      collapsedWidth={80}
      collapsed={collapsed}
      trigger={null}
      className="left-layout-sider"
    >
      <div className="sidebar-container">
        {/* Logo Section — Afya Tech mark (viewBox-cropped copy of the viewer's
            Afya_Tech_Logo.svg) + the same two-row wordmark the spike viewer
            uses ("AfyaNX" in brand teal over a small light tagline). */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-inner">
            <img src={afyaLogo} alt="AfyaNX" className="sidebar-logo-mark" />
            {!collapsed && (
              <div className="sidebar-logo-text">
                <h1>AfyaNX</h1>
                <p>PACS System</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navItems.map((item) => {
            if (!item.isShow) return null;
            const Icon = item.icon;
            const isActive = selectedKeys === item.route;
            return (
              <button
                key={item.route}
                onClick={() => handleNavClick(item.route)}
                className={`sidebar-nav-item${isActive ? ' active' : ''}`}
              >
                <Icon className="sidebar-nav-icon" />
                {!collapsed && (
                  <div className="sidebar-nav-text">
                    <div className="sidebar-nav-label">{item.label}</div>
                    <div className="sidebar-nav-desc">{item.description}</div>
                  </div>
                )}
                {isActive && <div className="sidebar-nav-indicator" />}
              </button>
            );
          })}
        </nav>

        {/* User Profile with Logout */}
        <div className="sidebar-user">
          <Dropdown overlay={userMenu} trigger={['click']} placement="topLeft">
            <div className="sidebar-user-card">
              <div className="sidebar-user-avatar">
                <User className="sidebar-user-avatar-icon" />
              </div>
              {!collapsed && (
                <div className="sidebar-user-info">
                  <div className="sidebar-user-name">{userName}</div>
                  <div className="sidebar-user-email">{userEmail}</div>
                </div>
              )}
            </div>
          </Dropdown>
        </div>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="sidebar-toggle"
      >
        {collapsed ? (
          <ChevronRight className="sidebar-toggle-icon" />
        ) : (
          <ChevronLeft className="sidebar-toggle-icon" />
        )}
      </button>
    </Layout.Sider>
  );
};

export default connect(
  (state) => ({
    userInfo: state.system.profile,
    profile: state.system.profile,
  }),
  null
)(withRouter(LeftMenu));
